import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { RegisterClass } from '@memberjunction/global';
import { BaseResourceComponent } from '@memberjunction/ng-shared';
import { RunQuery } from '@memberjunction/core';
import { BCBaseComponent } from '@bluecypress/bcsaas-ng-core';
import { BCTenantContext } from '@bluecypress/bcsaas-core';

// ── Types ──

type SubTab = 'activity' | 'components' | 'costs';
type ConversationView = 'list' | 'detail';

interface PersonActivity {
  personID: string;
  personName: string;
  conversationCount: number;
}

interface ConversationListItem {
  conversationID: string;
  name: string | null;
  personID: string;
  personName: string;
  exchangeCount: number;
  hasFailures: boolean;
  createdAt: string;
}

interface DailyActivityRow {
  day: string;
  conversationCount: number;
}

interface Exchange {
  id: string;
  role: string;
  message: string | null;
  personID: string | null;
  aiAgentRunID: string | null;
  userComponentVersionID: string | null;
  createdAt: string;
  agentRunStatus: string | null;
  agentRunError: string | null;
  agentRunDurationMs: number | null;
}

interface ComponentListItem {
  componentID: string;
  componentName: string;
  type: string | null;
  title: string | null;
  versionCount: number;
  avgRating: number | null;
  totalFeedbackCount: number;
  totalViews: number;
  lastGenerated: string | null;
}

interface DailyCostRow {
  day: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

interface PersonCostRow {
  personID: string;
  personName: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

interface ModelCostRow {
  modelName: string;
  vendorName: string;
  totalCost: number;
  totalTokens: number;
  runCount: number;
}

interface PerformanceData {
  componentsProduced: number;
  avgProductionTimeMs: number;
  minProductionTimeMs: number;
  maxProductionTimeMs: number;
}

@Component({
  standalone: false,
  selector: 'skip-analytics',
  templateUrl: './skip-analytics.component.html',
  styleUrls: ['./skip-analytics.component.css']
})
@RegisterClass(BaseResourceComponent, 'SkipAnalyticsResource')
export class SkipAnalyticsComponent extends BCBaseComponent implements OnInit, OnDestroy {
  // Authorization
  IsInitializing = true;
  IsAuthorizedUser = false;

  // Org context
  CurrentOrgID: string | undefined;
  CurrentOrgName: string | null = null;

  // Sub-tab
  ActiveTab: SubTab = 'activity';

  // Month selector
  SelectedYear: number;
  SelectedMonth: number; // 0-indexed
  MonthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  AvailableYears: number[] = [];

  // Error
  Error: string | null = null;

  // ── Activity tab ──
  Persons: PersonActivity[] = [];
  Conversations: ConversationListItem[] = [];
  DailyActivity: DailyActivityRow[] = [];
  SelectedPersonID = '';
  ActivityLoading = false;
  ConversationsLoading = false;
  MaxDailyActivity = 0;
  NiceMaxActivity = 0;

  // Conversation detail
  ConversationView: ConversationView = 'list';
  SelectedConversation: ConversationListItem | null = null;
  Exchanges: Exchange[] = [];
  ExchangesLoading = false;

  // ── Components tab ──
  Components: ComponentListItem[] = [];
  FilteredComponents: ComponentListItem[] = [];
  ComponentsLoading = false;
  ComponentSearch = '';
  ComponentSortColumn: keyof ComponentListItem = 'lastGenerated';
  ComponentSortDirection: 'asc' | 'desc' = 'desc';
  ComponentTypeFilter = '';

  // ── Costs tab ──
  DailyCosts: DailyCostRow[] = [];
  FilledDailyCosts: DailyCostRow[] = [];
  FilledDailyActivity: DailyActivityRow[] = [];
  ContactCosts: PersonCostRow[] = [];
  ModelCosts: ModelCostRow[] = [];
  CostLoading = false;
  MonthlyTotalCost = 0;
  MonthlyTotalTokens = 0;
  MonthlyTotalRuns = 0;
  MaxDailyCost = 0;
  NiceMaxCost = 0;
  MaxDailyUsage = 0;
  NiceMaxUsage = 0;
  PerformanceData: PerformanceData | null = null;
  PerformanceLoading = false;

  private cdr = inject(ChangeDetectorRef);

  constructor() {
    super();
    const now = new Date();
    this.SelectedYear = now.getFullYear();
    this.SelectedMonth = now.getMonth();
    this.buildAvailableYears();
  }

  async GetResourceDisplayName(): Promise<string> {
    return 'Organization Analytics';
  }

  async GetResourceIconClass(): Promise<string> {
    return 'fa-solid fa-chart-bar';
  }

  // ========== Lifecycle ==========

  override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.resolveOrgContext();
    this.IsInitializing = false;
    this.cdr.detectChanges();

    if (this.IsAuthorizedUser && this.CurrentOrgID) {
      await this.LoadAllData();
    }
    this.NotifyLoadComplete();
  }

  private resolveOrgContext(): void {
    const ctx = this.tenantContextService.currentContext;
    const memberships = this.tenantContextService.GetAllMemberships();
    if (ctx?.organizationID) {
      this.IsAuthorizedUser = true;
      this.CurrentOrgID = ctx.organizationID;
      this.CurrentOrgName = ctx.organizationName || null;
    } else if (memberships.length > 0) {
      this.IsAuthorizedUser = true;
      this.CurrentOrgID = memberships[0].organizationID;
      this.CurrentOrgName = memberships[0].organizationName || null;
    } else {
      this.IsAuthorizedUser = false;
    }
  }

  protected override onContextChanged(ctx: BCTenantContext | null): void {
    if (ctx?.organizationID) {
      this.CurrentOrgID = ctx.organizationID;
      this.CurrentOrgName = ctx.organizationName || null;
      this.LoadAllData();
      this.cdr.detectChanges();
    }
  }

  // ========== Month Navigation ==========

  private buildAvailableYears(): void {
    const currentYear = new Date().getFullYear();
    this.AvailableYears = [];
    for (let y = 2024; y <= currentYear; y++) {
      this.AvailableYears.push(y);
    }
  }

  PrevMonth(): void {
    if (this.SelectedMonth === 0) {
      this.SelectedMonth = 11;
      this.SelectedYear--;
    } else {
      this.SelectedMonth--;
    }
    this.LoadAllData();
  }

  NextMonth(): void {
    if (this.IsCurrentMonth) return;
    if (this.SelectedMonth === 11) {
      this.SelectedMonth = 0;
      this.SelectedYear++;
    } else {
      this.SelectedMonth++;
    }
    this.LoadAllData();
  }

  OnMonthSelect(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.SelectedMonth = parseInt(value, 10);
    this.clampToCurrentMonth();
    this.LoadAllData();
  }

  OnYearSelect(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.SelectedYear = parseInt(value, 10);
    this.clampToCurrentMonth();
    this.LoadAllData();
  }

  GoToCurrentMonth(): void {
    const now = new Date();
    this.SelectedYear = now.getFullYear();
    this.SelectedMonth = now.getMonth();
    this.LoadAllData();
  }

  private clampToCurrentMonth(): void {
    const now = new Date();
    if (this.SelectedYear > now.getFullYear()) {
      this.SelectedYear = now.getFullYear();
      this.SelectedMonth = now.getMonth();
    } else if (this.SelectedYear === now.getFullYear() && this.SelectedMonth > now.getMonth()) {
      this.SelectedMonth = now.getMonth();
    }
  }

  IsMonthDisabled(monthIndex: number): boolean {
    const now = new Date();
    return this.SelectedYear === now.getFullYear() && monthIndex > now.getMonth();
  }

  get IsCurrentMonth(): boolean {
    const now = new Date();
    return this.SelectedYear === now.getFullYear() && this.SelectedMonth === now.getMonth();
  }

  get MonthLabel(): string {
    return `${this.MonthNames[this.SelectedMonth]} ${this.SelectedYear}`;
  }

  private getMonthRange(): { start: string; end: string } {
    const start = new Date(this.SelectedYear, this.SelectedMonth, 1);
    const end = new Date(this.SelectedYear, this.SelectedMonth + 1, 1);
    const now = new Date();
    const effectiveEnd = end > now ? now : end;
    return { start: start.toISOString(), end: effectiveEnd.toISOString() };
  }

  // ========== Tab Switching ==========

  SwitchTab(tab: SubTab): void {
    this.ActiveTab = tab;
    this.loadTabData();
  }

  private loadTabData(): void {
    switch (this.ActiveTab) {
      case 'activity':
        this.loadActivityTab();
        break;
      case 'components':
        this.loadComponentsTab();
        break;
      case 'costs':
        this.loadCostsTab();
        break;
    }
  }

  // ========== Master Data Load ==========

  async LoadAllData(): Promise<void> {
    if (!this.CurrentOrgID) return;
    this.Error = null;
    this.ConversationView = 'list';
    this.SelectedConversation = null;
    this.loadTabData();
  }

  // ========== Activity Tab ==========

  private async loadActivityTab(): Promise<void> {
    if (!this.CurrentOrgID) return;
    await Promise.all([
      this.loadPersonActivities(),
      this.loadConversations()
    ]);
  }

  private async loadPersonActivities(): Promise<void> {
    if (!this.CurrentOrgID) return;
    this.ActivityLoading = true;
    this.cdr.detectChanges();

    try {
      const { start, end } = this.getMonthRange();
      const rq = new RunQuery();
      const result = await rq.RunQuery({
        QueryName: 'OrgAnalyticsUsageMetrics',
        CategoryPath: '/Skip/OrgAnalytics',
        Parameters: {
          OrganizationID: this.CurrentOrgID,
          MonthStart: start,
          MonthEnd: end
        }
      });

      this.Persons = [];
      this.DailyActivity = [];
      this.MaxDailyActivity = 0;

      if (result.Success && result.Results?.length) {
        this.parseUsageResults(result.Results);
      }
      this.NiceMaxActivity = this.computeNiceMax(this.MaxDailyActivity);
      this.buildFilledDailyActivity();
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load activity data';
    } finally {
      this.ActivityLoading = false;
      this.cdr.detectChanges();
    }
  }

  private parseUsageResults(results: Record<string, unknown>[]): void {
    for (const row of results) {
      const granularity = row['Granularity'] as string;
      if (granularity === 'Contact') {
        this.Persons.push({
          personID: row['PersonID'] as string,
          personName: (row['PersonName'] as string) || 'Unknown',
          conversationCount: (row['ConversationCount'] as number) || 0
        });
      } else if (granularity === 'Daily') {
        const count = (row['ConversationCount'] as number) || 0;
        this.DailyActivity.push({
          day: row['Day'] as string,
          conversationCount: count
        });
        if (count > this.MaxDailyActivity) this.MaxDailyActivity = count;
      }
    }
  }

  private async loadConversations(): Promise<void> {
    if (!this.CurrentOrgID) return;
    this.ConversationsLoading = true;
    this.cdr.detectChanges();

    try {
      const { start, end } = this.getMonthRange();
      const rq = new RunQuery();
      const params: Record<string, string> = {
        OrganizationID: this.CurrentOrgID,
        StartDate: start,
        EndDate: end
      };
      if (this.SelectedPersonID) {
        params['PersonID'] = this.SelectedPersonID;
      }

      const result = await rq.RunQuery({
        QueryName: 'MonitoringOrgConversations',
        CategoryPath: '/Skip/Monitoring',
        Parameters: params
      });

      if (result.Success && result.Results?.length) {
        this.Conversations = result.Results.map((r: Record<string, unknown>) =>
          this.mapConversationItem(r)
        );
      } else {
        this.Conversations = [];
      }
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load conversations';
    } finally {
      this.ConversationsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapConversationItem(r: Record<string, unknown>): ConversationListItem {
    return {
      conversationID: r['ConversationID'] as string,
      name: (r['Name'] as string) || null,
      personID: r['PersonID'] as string,
      personName: (r['PersonName'] as string) || 'Unknown',
      exchangeCount: (r['ExchangeCount'] as number) || 0,
      hasFailures: (r['HasFailures'] as number) === 1,
      createdAt: r['CreatedAt'] as string
    };
  }

  FilterByPerson(personID: string): void {
    this.SelectedPersonID = this.SelectedPersonID === personID ? '' : personID;
    this.loadConversations();
  }

  ClearPersonFilter(): void {
    this.SelectedPersonID = '';
    this.loadConversations();
  }

  // ========== Conversation Detail ==========

  async SelectConversation(conv: ConversationListItem): Promise<void> {
    this.SelectedConversation = conv;
    this.ConversationView = 'detail';
    this.Exchanges = [];
    this.ExchangesLoading = true;
    this.cdr.detectChanges();

    try {
      const rq = new RunQuery();
      const result = await rq.RunQuery({
        QueryName: 'SkipConversationHistory',
        CategoryPath: '/Skip/Monitoring',
        Parameters: { ConversationID: conv.conversationID }
      });

      if (result.Success && result.Results?.length) {
        this.Exchanges = result.Results.map((r: Record<string, unknown>) =>
          this.mapExchange(r)
        );
      }
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load conversation history';
    } finally {
      this.ExchangesLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapExchange(r: Record<string, unknown>): Exchange {
    return {
      id: r['ID'] as string,
      role: r['Role'] as string,
      message: (r['Message'] as string) || null,
      personID: (r['PersonID'] as string) || null,
      aiAgentRunID: (r['AIAgentRunID'] as string) || null,
      userComponentVersionID: (r['UserComponentVersionID'] as string) || null,
      createdAt: r['CreatedAt'] as string,
      agentRunStatus: (r['AgentRunStatus'] as string) || null,
      agentRunError: (r['AgentRunError'] as string) || null,
      agentRunDurationMs: (r['AgentRunDurationMs'] as number) || null
    };
  }

  BackToConversationList(): void {
    this.ConversationView = 'list';
    this.SelectedConversation = null;
    this.Exchanges = [];
  }

  // ========== Components Tab ==========

  private async loadComponentsTab(): Promise<void> {
    if (!this.CurrentOrgID) return;
    this.ComponentsLoading = true;
    this.Error = null;
    this.cdr.detectChanges();

    try {
      const { start, end } = this.getMonthRange();
      const rq = new RunQuery();
      const result = await rq.RunQuery({
        QueryName: 'MonitoringOrgComponents',
        CategoryPath: '/Skip/Monitoring',
        Parameters: {
          OrganizationID: this.CurrentOrgID,
          StartDate: start,
          EndDate: end
        }
      });

      if (result.Success && result.Results?.length) {
        this.Components = result.Results.map((r: Record<string, unknown>) =>
          this.mapComponentItem(r)
        );
      } else {
        this.Components = [];
      }
      this.ApplyComponentFilters();
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load components';
    } finally {
      this.ComponentsLoading = false;
      this.cdr.detectChanges();
    }
  }

  private mapComponentItem(r: Record<string, unknown>): ComponentListItem {
    return {
      componentID: r['ComponentID'] as string,
      componentName: (r['ComponentName'] as string) || 'Unnamed',
      type: (r['Type'] as string) || null,
      title: (r['Title'] as string) || null,
      versionCount: (r['VersionCount'] as number) || 0,
      avgRating: (r['AvgRating'] as number) || null,
      totalFeedbackCount: (r['TotalFeedbackCount'] as number) || 0,
      totalViews: (r['TotalViews'] as number) || 0,
      lastGenerated: (r['LastGenerated'] as string) || null
    };
  }

  ApplyComponentFilters(): void {
    let filtered = [...this.Components];
    filtered = this.filterComponentsBySearch(filtered);
    filtered = this.filterComponentsByType(filtered);
    filtered = this.sortComponents(filtered);
    this.FilteredComponents = filtered;
  }

  private filterComponentsBySearch(items: ComponentListItem[]): ComponentListItem[] {
    const term = this.ComponentSearch.toLowerCase().trim();
    if (!term) return items;
    return items.filter(c =>
      c.componentName.toLowerCase().includes(term) ||
      (c.title && c.title.toLowerCase().includes(term)) ||
      (c.type && c.type.toLowerCase().includes(term))
    );
  }

  private filterComponentsByType(items: ComponentListItem[]): ComponentListItem[] {
    if (!this.ComponentTypeFilter) return items;
    return items.filter(c => c.type === this.ComponentTypeFilter);
  }

  private sortComponents(items: ComponentListItem[]): ComponentListItem[] {
    const col = this.ComponentSortColumn;
    const dir = this.ComponentSortDirection === 'asc' ? 1 : -1;
    return items.sort((a, b) => {
      const aVal = a[col];
      const bVal = b[col];
      if (aVal === null || aVal === undefined) return dir;
      if (bVal === null || bVal === undefined) return -dir;
      if (typeof aVal === 'string') return aVal.localeCompare(bVal as string) * dir;
      return ((aVal as number) - (bVal as number)) * dir;
    });
  }

  SetComponentSort(column: keyof ComponentListItem): void {
    if (this.ComponentSortColumn === column) {
      this.ComponentSortDirection = this.ComponentSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.ComponentSortColumn = column;
      this.ComponentSortDirection = column === 'componentName' ? 'asc' : 'desc';
    }
    this.ApplyComponentFilters();
  }

  GetComponentSortIcon(column: string): string {
    if (this.ComponentSortColumn !== column) return 'fa-sort';
    return this.ComponentSortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  get ComponentTypes(): string[] {
    const types = new Set<string>();
    for (const c of this.Components) {
      if (c.type) types.add(c.type);
    }
    return Array.from(types).sort();
  }

  GetStarArray(rating: number | null): boolean[] {
    if (rating === null) return [false, false, false, false, false];
    const rounded = Math.round(rating);
    return [1, 2, 3, 4, 5].map(i => i <= rounded);
  }

  // ========== Costs Tab ==========

  private async loadCostsTab(): Promise<void> {
    if (!this.CurrentOrgID) return;
    this.CostLoading = true;
    this.PerformanceLoading = true;
    this.Error = null;
    this.cdr.detectChanges();

    await Promise.all([
      this.loadCostMetrics(),
      this.loadPerformanceMetrics()
    ]);
  }

  private async loadCostMetrics(): Promise<void> {
    try {
      const { start, end } = this.getMonthRange();
      const rq = new RunQuery();
      const result = await rq.RunQuery({
        QueryName: 'OrgAnalyticsCostMetrics',
        CategoryPath: '/Skip/OrgAnalytics',
        Parameters: {
          OrganizationID: this.CurrentOrgID,
          MonthStart: start,
          MonthEnd: end
        }
      });

      this.resetCostState();
      if (result.Success && result.Results?.length) {
        this.parseCostResults(result.Results);
      }
      this.buildFilledDailyCosts();
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load cost metrics';
    } finally {
      this.CostLoading = false;
      this.cdr.detectChanges();
    }
  }

  private resetCostState(): void {
    this.DailyCosts = [];
    this.ContactCosts = [];
    this.ModelCosts = [];
    this.MonthlyTotalCost = 0;
    this.MonthlyTotalTokens = 0;
    this.MonthlyTotalRuns = 0;
    this.MaxDailyCost = 0;
  }

  private parseCostResults(results: Record<string, unknown>[]): void {
    for (const row of results) {
      const granularity = row['Granularity'] as string;
      if (granularity === 'Daily') {
        const cost = (row['TotalCost'] as number) || 0;
        this.DailyCosts.push({
          day: row['Day'] as string,
          totalCost: cost,
          totalTokens: (row['TotalTokens'] as number) || 0,
          runCount: (row['RunCount'] as number) || 0
        });
        if (cost > this.MaxDailyCost) this.MaxDailyCost = cost;
      } else if (granularity === 'Contact') {
        this.ContactCosts.push({
          personID: row['PersonID'] as string,
          personName: (row['PersonName'] as string) || 'Unknown',
          totalCost: (row['TotalCost'] as number) || 0,
          totalTokens: (row['TotalTokens'] as number) || 0,
          runCount: (row['RunCount'] as number) || 0
        });
      } else if (granularity === 'Model') {
        this.ModelCosts.push({
          modelName: (row['ModelName'] as string) || 'Unknown',
          vendorName: (row['VendorName'] as string) || 'Unknown',
          totalCost: (row['TotalCost'] as number) || 0,
          totalTokens: (row['TotalTokens'] as number) || 0,
          runCount: (row['RunCount'] as number) || 0
        });
      }
    }
    this.ModelCosts.sort((a, b) => b.totalCost - a.totalCost);
    this.MonthlyTotalCost = this.DailyCosts.reduce((sum, d) => sum + d.totalCost, 0);
    this.MonthlyTotalTokens = this.DailyCosts.reduce((sum, d) => sum + d.totalTokens, 0);
    this.MonthlyTotalRuns = this.DailyCosts.reduce((sum, d) => sum + d.runCount, 0);
  }

  private async loadPerformanceMetrics(): Promise<void> {
    try {
      const { start, end } = this.getMonthRange();
      const rq = new RunQuery();
      const result = await rq.RunQuery({
        QueryName: 'OrgAnalyticsPerformanceMetrics',
        CategoryPath: '/Skip/OrgAnalytics',
        Parameters: {
          OrganizationID: this.CurrentOrgID,
          MonthStart: start,
          MonthEnd: end
        }
      });

      this.PerformanceData = null;
      if (result.Success && result.Results?.length) {
        const row = result.Results[0];
        this.PerformanceData = {
          componentsProduced: (row['ComponentsProduced'] as number) || 0,
          avgProductionTimeMs: (row['AvgProductionTimeMs'] as number) || 0,
          minProductionTimeMs: (row['MinProductionTimeMs'] as number) || 0,
          maxProductionTimeMs: (row['MaxProductionTimeMs'] as number) || 0
        };
      }
    } catch (e: unknown) {
      this.Error = e instanceof Error ? e.message : 'Failed to load performance metrics';
    } finally {
      this.PerformanceLoading = false;
      this.cdr.detectChanges();
    }
  }

  // ========== Chart Builders ==========

  private buildFilledDailyCosts(): void {
    const maxDay = this.getMaxDayOfMonth();
    const costMap = new Map<number, DailyCostRow>();
    for (const row of this.DailyCosts) {
      costMap.set(new Date(row.day).getDate(), row);
    }

    this.FilledDailyCosts = [];
    for (let day = 1; day <= maxDay; day++) {
      const existing = costMap.get(day);
      if (existing) {
        this.FilledDailyCosts.push(existing);
      } else {
        this.FilledDailyCosts.push({
          day: new Date(this.SelectedYear, this.SelectedMonth, day).toISOString(),
          totalCost: 0,
          totalTokens: 0,
          runCount: 0
        });
      }
    }
    this.NiceMaxCost = this.computeNiceMax(this.MaxDailyCost);
  }

  private buildFilledDailyActivity(): void {
    const maxDay = this.getMaxDayOfMonth();
    const usageMap = new Map<number, DailyActivityRow>();
    for (const row of this.DailyActivity) {
      usageMap.set(new Date(row.day).getDate(), row);
    }

    this.FilledDailyActivity = [];
    this.MaxDailyUsage = 0;
    for (let day = 1; day <= maxDay; day++) {
      const existing = usageMap.get(day);
      if (existing) {
        this.FilledDailyActivity.push(existing);
        if (existing.conversationCount > this.MaxDailyUsage) {
          this.MaxDailyUsage = existing.conversationCount;
        }
      } else {
        this.FilledDailyActivity.push({
          day: new Date(this.SelectedYear, this.SelectedMonth, day).toISOString(),
          conversationCount: 0
        });
      }
    }
    this.NiceMaxUsage = this.computeNiceMax(this.MaxDailyUsage);
  }

  private getMaxDayOfMonth(): number {
    const now = new Date();
    const daysInMonth = new Date(this.SelectedYear, this.SelectedMonth + 1, 0).getDate();
    return this.IsCurrentMonth ? Math.min(now.getDate(), daysInMonth) : daysInMonth;
  }

  private computeNiceMax(maxValue: number): number {
    if (maxValue <= 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    const normalized = maxValue / magnitude;
    if (normalized <= 1) return magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 2.5) return 2.5 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
  }

  // ========== Chart Helpers ==========

  get CostYAxisTicks(): number[] {
    if (!this.NiceMaxCost) return [0];
    const step = this.NiceMaxCost / 4;
    return [0, step, step * 2, step * 3, this.NiceMaxCost];
  }

  get UsageYAxisTicks(): number[] {
    if (!this.NiceMaxUsage) return [0];
    const step = this.NiceMaxUsage / 4;
    return [0, step, step * 2, step * 3, this.NiceMaxUsage];
  }

  get ActivityYAxisTicks(): number[] {
    if (!this.NiceMaxActivity) return [0];
    const step = this.NiceMaxActivity / 4;
    return [0, step, step * 2, step * 3, this.NiceMaxActivity];
  }

  GetCostBarHeight(cost: number): number {
    if (!this.NiceMaxCost || !cost) return 0;
    return (cost / this.NiceMaxCost) * 100;
  }

  GetUsageBarHeight(count: number): number {
    if (!this.NiceMaxUsage || !count) return 0;
    return (count / this.NiceMaxUsage) * 100;
  }

  GetActivityBarHeight(count: number): number {
    if (!this.NiceMaxActivity || !count) return 0;
    return (count / this.NiceMaxActivity) * 100;
  }

  GetGridlinePosition(tick: number, niceMax: number): number {
    if (!niceMax) return 0;
    return (tick / niceMax) * 100;
  }

  get MaxModelCost(): number {
    if (!this.ModelCosts.length) return 0;
    return this.ModelCosts[0].totalCost;
  }

  get MaxContactCost(): number {
    if (!this.ContactCosts.length) return 0;
    return Math.max(...this.ContactCosts.map(c => c.totalCost));
  }

  GetModelBarPercent(cost: number): number {
    if (!this.MaxModelCost || !cost) return 0;
    return (cost / this.MaxModelCost) * 100;
  }

  GetContactCostBarPercent(cost: number): number {
    if (!this.MaxContactCost || !cost) return 0;
    return (cost / this.MaxContactCost) * 100;
  }

  GetShortDay(iso: string): string {
    if (!iso) return '';
    return new Date(iso).getDate().toString();
  }

  // ========== Display Helpers ==========

  FormatDate(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  }

  FormatShortDate(iso: string | null): string {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  FormatCost(cost: number): string {
    if (!cost) return '$0.00';
    return `$${cost.toFixed(2)}`;
  }

  FormatCostPrecise(cost: number): string {
    if (!cost) return '$0.0000';
    return `$${cost.toFixed(4)}`;
  }

  FormatAxisCost(cost: number): string {
    if (cost === 0) return '$0';
    if (cost >= 100) return `$${Math.round(cost)}`;
    if (cost >= 10) return `$${cost.toFixed(1)}`;
    return `$${cost.toFixed(2)}`;
  }

  FormatAxisCount(count: number): string {
    if (count === 0) return '0';
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    if (Number.isInteger(count)) return count.toString();
    return count.toFixed(1);
  }

  FormatTokens(count: number): string {
    if (!count) return '0';
    if (count >= 1e6) return `${(count / 1e6).toFixed(1)}M`;
    if (count >= 1e3) return `${(count / 1e3).toFixed(1)}K`;
    return count.toString();
  }

  FormatDuration(ms: number | null): string {
    if (ms === null) return '\u2014';
    if (ms < 1000) return `${ms}ms`;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  FormatDurationMinSec(ms: number): string {
    if (!ms) return '\u2014';
    const totalSec = Math.round(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  }

  GetStatusClass(status: string | null): string {
    if (!status) return '';
    const map: Record<string, string> = {
      Running: 'status-running',
      Completed: 'status-completed',
      Failed: 'status-failed',
      Cancelled: 'status-cancelled'
    };
    return map[status] || '';
  }

  GetDurationClass(ms: number | null): string {
    if (ms === null) return '';
    if (ms > 300000) return 'duration-critical';
    if (ms > 120000) return 'duration-warning';
    return '';
  }

  IsFormMessage(message: string): boolean {
    return message?.startsWith('@{') || message?.startsWith('@{"_mode"');
  }

  ParseFormFields(message: string): { label: string; displayValue: string }[] {
    try {
      const json = JSON.parse(message.substring(1));
      if (json.fields && Array.isArray(json.fields)) {
        return json.fields.map((f: Record<string, string>) => ({
          label: f['label'] || f['name'] || 'Field',
          displayValue: f['displayValue'] || f['value'] || ''
        }));
      }
    } catch {
      // invalid JSON, return empty
    }
    return [];
  }

  GetMessagePreview(message: string | null): string {
    if (!message) return '\u2014';
    if (this.IsFormMessage(message)) return 'Filled out Form';
    return message;
  }

  // ========== CSV Export ==========

  ExportCSV(): void {
    const headers = ['Type', 'Date/User/Model', 'Cost', 'Tokens', 'Runs/Conversations'];
    const rows: string[][] = [];

    this.appendActivityCSVRows(rows);
    this.appendComponentCSVRows(rows);
    this.appendCostCSVRows(rows);

    if (rows.length === 0) return;

    const csv = [
      headers.join(','),
      ...rows.map(r => r.map(v => `"${v.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    this.downloadCSV(csv);
  }

  private appendActivityCSVRows(rows: string[][]): void {
    for (const d of this.DailyActivity) {
      rows.push(['Daily Activity', this.FormatShortDate(d.day), '', '', d.conversationCount.toString()]);
    }
    for (const p of this.Persons) {
      rows.push(['User Activity', p.personName, '', '', p.conversationCount.toString()]);
    }
  }

  private appendComponentCSVRows(rows: string[][]): void {
    for (const c of this.Components) {
      rows.push([
        'Component',
        c.componentName,
        c.type || '',
        c.versionCount.toString(),
        c.totalViews.toString()
      ]);
    }
  }

  private appendCostCSVRows(rows: string[][]): void {
    for (const d of this.DailyCosts) {
      rows.push(['Daily Cost', this.FormatShortDate(d.day), this.FormatCostPrecise(d.totalCost), d.totalTokens.toString(), d.runCount.toString()]);
    }
    for (const c of this.ContactCosts) {
      rows.push(['User Cost', c.personName, this.FormatCostPrecise(c.totalCost), c.totalTokens.toString(), c.runCount.toString()]);
    }
    for (const m of this.ModelCosts) {
      rows.push(['Model Cost', `${m.modelName} (${m.vendorName})`, this.FormatCostPrecise(m.totalCost), m.totalTokens.toString(), m.runCount.toString()]);
    }
  }

  private downloadCSV(csv: string): void {
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skip-analytics-${this.CurrentOrgName || 'org'}-${this.SelectedYear}-${String(this.SelectedMonth + 1).padStart(2, '0')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
